import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import {
  ShoppingBag, Banknote, Truck, Rocket, Building2,
  Stethoscope, GraduationCap, Home, Factory, Hotel,
} from 'lucide-react';

const GOLD = '#2563EB';
const GOLD_SOFT = '#3B82F6';

const industries = [
  { icon: ShoppingBag,    title: 'Retail & E-Commerce',     description: 'Digital storefronts, inventory systems, omnichannel commerce.' },
  { icon: Banknote,       title: 'Finance & FinTech',        description: 'Secure payment platforms, trading systems, compliance tools.' },
  { icon: Truck,          title: 'Logistics & Supply Chain', description: 'Fleet management, tracking systems, warehouse automation.' },
  { icon: Rocket,         title: 'Startups & SaaS',          description: 'MVP development, rapid prototyping, scalable architectures.' },
  { icon: Building2,      title: 'Enterprises & SMEs',       description: 'Custom ERP systems, digital transformation, legacy modernization.' },
  { icon: Stethoscope,    title: 'Healthcare Tech',          description: 'Patient platforms, clinic management, telehealth integration.' },
  { icon: GraduationCap,  title: 'Education & EdTech',       description: 'LMS platforms, online learning tools, school management systems.' },
  { icon: Home,           title: 'Real Estate & PropTech',   description: 'Property listings, CRM platforms, virtual tour integration.' },
  { icon: Factory,        title: 'Manufacturing',            description: 'Production tracking, ERP integration, IoT and automation.' },
  { icon: Hotel,          title: 'Hospitality & Travel',     description: 'Booking engines, PMS integration, guest experience platforms.' },
];

const tickerItems = [
  'Retail & E-Commerce', 'Finance & FinTech', 'Logistics & Supply Chain',
  'Startups & SaaS', 'Enterprises & SMEs', 'Healthcare Tech', 'EdTech',
  'Real Estate', 'Manufacturing', 'PropTech', 'InsurTech', 'GovTech',
  'Hospitality', 'Energy', 'Media',
];

const IndustryCard = ({ industry, index }) => {
  const Icon = industry.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="group bg-[#0F172A] p-7 hover:bg-[#142F5C] transition-colors duration-300 relative overflow-hidden cursor-default"
      data-testid={`industry-card-${index}`}
    >
      {/* Top gold accent on hover */}
      <div
        className="absolute top-0 left-0 right-0 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left"
        style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
      />

      {/* Corner bracket */}
      <div
        className="absolute top-0 right-0 w-8 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-right"
        style={{ background: GOLD_SOFT }}
      />
      <div
        className="absolute top-0 right-0 w-px h-8 scale-y-0 group-hover:scale-y-100 transition-transform duration-400 origin-top"
        style={{ background: GOLD_SOFT }}
      />

      <div className="relative">
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 280, damping: 14 }}
          className="w-10 h-10 flex items-center justify-center mb-5 border border-[#1E40AF] group-hover:border-[#2563EB]/40 transition-all duration-300"
          style={{ backgroundColor: 'rgba(37,99,235, 0.10)' }}
        >
          <Icon size={18} className="text-[#2563EB] group-hover:text-[#3B82F6] transition-colors duration-300" />
        </motion.div>
        <h3 className="text-white font-semibold mb-2 text-[0.95rem] group-hover:text-[#3B82F6] transition-colors duration-300">
          {industry.title}
        </h3>
        <p className="text-[#64748B] text-sm leading-relaxed group-hover:text-[#94A3B8] transition-colors duration-300">
          {industry.description}
        </p>
      </div>
    </motion.div>
  );
};

export const IndustriesSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const glowY = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);

  return (
    <section
      ref={sectionRef}
      id="industries"
      className="section-padding bg-[#0F172A] relative overflow-hidden"
      data-testid="industries-section"
    >
      {/* Ambient glows — warm gold + navy for depth */}
      <motion.div
        style={{ y: glowY }}
        className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#2563EB]/[0.05] blur-[130px] pointer-events-none"
      />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#3B82F6]/[0.15] blur-[110px] pointer-events-none" />

      {/* Faint grid bg */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(37,99,235,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.05) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      {/* Header */}
      <div className="container-custom relative z-10 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="section-label" style={{ color: '#3B82F6' }}>Domain Expertise</span>
            <span className="w-6 h-px bg-[#3B82F6]/30" />
            <span className="font-mono text-[0.55rem] tracking-widest text-[#475569] uppercase">
              10+ Verticals
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-display font-bold text-white"
              style={{ fontSize: 'clamp(2rem, 4.4vw, 3.75rem)' }}
            >
              Industries We{' '}
              <span
                className="italic"
                style={{
                  background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Serve
              </span>
            </h2>
            <p className="text-[#94A3B8] max-w-sm leading-relaxed text-sm lg:text-right">
              Deep domain expertise across diverse sectors — delivering tailored solutions that fit each industry's unique workflows and constraints.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Marquee ticker */}
      <div className="overflow-hidden border-y border-[#1E40AF] py-5 mb-16 relative z-10">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-5 mx-6 font-display italic"
              style={{
                fontSize: '1.05rem',
                color: i % 5 === 0 ? '#3B82F6' : '#334155',
              }}
            >
              {item}
              <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#1E40AF]" />
            </span>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#1E40AF]">
          {industries.map((industry, i) => (
            <IndustryCard key={industry.title} industry={industry} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
